import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Aurum from '../../assets/cosmetics-display/aurum.png';
import BlackMarble from '../../assets/cosmetics-display/black-marble.png';
import BlackVelvet from '../../assets/cosmetics-display/black-velvet.png';
import Cadenza from '../../assets/cosmetics-display/cadenza.png';
import Jade from '../../assets/cosmetics-display/jade.png';
import LouisPackard from '../../assets/cosmetics-display/louis-packard.png';
import Lustre from '../../assets/cosmetics-display/lustre.png';
import Marble from '../../assets/cosmetics-display/marble.png';
import Mushroom from '../../assets/cosmetics-display/mushroom.png';
import PlayTone from '../../assets/cosmetics-display/play-tone.png';
import RedVelvet from '../../assets/cosmetics-display/red-velvet.png';
import Rifle from '../../assets/cosmetics-display/rifle.png';
import Sonare from '../../assets/cosmetics-display/sonare.png';
import Spork from '../../assets/cosmetics-display/spork.png';
import Sullivan from '../../assets/cosmetics-display/sullivan.png';
import CosmeticsSign from '../../assets/cosmetics-sign.png';
import CosmeticsUnit from '../../components/CosmeticsUnit/CosmeticsUnit.jsx';
import GemIcon from '../../components/Idle/GemIcon';
import Loading from '../../components/Loading/Loading.jsx';
import { auth, db } from '../../lib/firebase';
import { spendGems, useIdle } from '../../lib/idle/idleStore';
import Styles from './Cosmetics.module.css';
import { useUI } from '../../lib/ui';

// Item prices below were written in coins. This turns them into gems (275 coins -> 11 gems).
// Change 25 to make everything cheaper or dearer.
const GEM_PRICE_DIVISOR = 25;
const gemPrice = (price) => (price > 0 ? Math.max(1, Math.round(price / GEM_PRICE_DIVISOR)) : price);

// data is an array at first and an object after a purchase, so keep whichever shape it has
const priced = (data) => {
  const out = Array.isArray(data) ? [] : {};
  Object.keys(data).forEach((k) => {
    out[k] = { ...data[k], price: gemPrice(data[k].price) };
  });
  return out;
};

const Cosmetics = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [membershipStatus, setMembershipStatus] = useState('Basic');
  const gems = useIdle((s) => s.gems);
  const openPricing = useUI((s) => s.openPricing);

  const navigate = useNavigate();

  const getItems = () => {
    return {
      valvedInstruments: {
        title: 'Valved Instruments',
        data: [
          {
            name: 'cadenza',
            title: 'Cadenza CDZ-2475 Student Bb Instrument',
            image: Cadenza,
            isOwned: true,
            price: -1,
            extraPadding: false,
          },
          {
            name: 'sonare',
            title: 'Sonare STR-5000S Professional Bb Instrument',
            image: Sonare,
            isOwned: false,
            price: 275,
            extraPadding: false,
          },
          {
            name: 'playTone',
            title: 'PlayTone Plastic Bb Instrument',
            image: PlayTone,
            isOwned: false,
            price: 435,
            extraPadding: true,
          },
          {
            name: 'mushroom',
            title: 'Frog & Mushroom',
            image: Mushroom,
            isOwned: false,
            price: 425,
            extraPadding: true,
          },
          {
            name: 'rifle',
            title: 'Colorguard Rifle',
            image: Rifle,
            isOwned: false,
            price: 195,
            extraPadding: true,
          },
        ],
      },
      valveFelts: {
        title: 'Valve Felts',
        data: [
          {
            name: 'RedVelvet',
            title: 'Red Velvet',
            image: RedVelvet,
            isOwned: true,
            price: -1,
            extraPadding: true,
          },
          {
            name: 'blackVelvet',
            title: 'Black Velvet',
            image: BlackVelvet,
            isOwned: false,
            price: 110,
            extraPadding: true,
          },
        ],
      },
      inlays: {
        title: 'Inlays',
        data: [
          {
            name: 'marble',
            title: 'Marble',
            image: Marble,
            isOwned: true,
            price: -1,
            extraPadding: true,
          },
          {
            name: 'blackMarble',
            title: 'Black Marble',
            image: BlackMarble,
            isOwned: false,
            price: 215,
            extraPadding: true,
          },
          {
            name: 'jade',
            title: 'Jade',
            image: Jade,
            isOwned: false,
            price: 370,
            extraPadding: true,
          },
        ],
      },
      rotaryValves: {
        title: 'Rotary Valves',
        data: [
          {
            name: 'lustre',
            title: 'W.H. Sullivan Professional French Horn',
            image: Sullivan,
            isOwned: true,
            price: -1,
          },
          {
            name: 'aurum',
            title: 'Louis Packard LP786 Student French Horn',
            image: LouisPackard,
            isOwned: false,
            price: 285,
            extraPadding: true,
          },
          {
            name: 'spork',
            title: 'Spork',
            image: Spork,
            isOwned: false,
            price: 300,
            extraPadding: true,
          },
        ],
      },
      slides: {
        title: 'Slides',
        data: [
          {
            name: 'lustre',
            title: 'Lustre LTB-120 Beginner Tenor Trombone',
            image: Lustre,
            isOwned: true,
            price: -1,
            extraPadding: true,
          },
          {
            name: 'aurum',
            title: 'Aurum 42A Professional Tenor Trombone',
            image: Aurum,
            isOwned: false,
            price: 415,
            extraPadding: true,
          },
        ],
      },
    };
  };

  const [items, setItems] = useState(getItems());

  const [selectedItems, setSelectedItems] = useState({
    valvedInstruments: 0,
    valveFelts: 0,
    inlays: 0,
    rotaryValves: 0,
    slides: 0,
  });

  const loadUserData = async () => {
    const user = auth.currentUser;

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();

      if (data.membershipStatus === 'Basic') {
        openPricing('Premium keeps your band playing while you are away.')
      }
      return {
        items: data.items || getItems(),
        selectedItems: data.selectedItems || {
          valvedInstruments: 0,
          valveFelts: 0,
          inlays: 0,
          rotaryValves: 0,
          slides: 0,
        },
        membershipStatus: data.membershipStatus || 'Basic',
      };
    }
  };

  // Gems live in the game store now, so this page only saves what you own and what's equipped
  const updateUserData = async (newItems, newSelectedItems) => {
    const user = auth.currentUser;

    if (user) {
      await setDoc(
        doc(db, 'users', user.uid),
        { items: newItems, selectedItems: newSelectedItems },
        { merge: true },
      );
    }
  };

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const { items, selectedItems, membershipStatus } = await loadUserData();
          setItems(items);
          setSelectedItems(selectedItems);
          setMembershipStatus(membershipStatus);

          const timer = setTimeout(() => {
            setIsLoading(false);
          }, 500);

          return () => {
            clearTimeout(timer);
          };
        }
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      updateUserData(items, selectedItems);
    }
  }, [items, selectedItems]);

  // obj.price here is already in gems, because the units are handed priced() copies
  const handleClick = (title, key, index, obj) => {
    if (obj.isOwned === false && gems >= obj.price) {
      if (title === 'Valved Instruments') {
        setItems((prev) => ({
          ...prev,
          valvedInstruments: {
            ...prev.valvedInstruments,
            data: {
              ...prev.valvedInstruments.data,
              [key]: {
                ...prev.valvedInstruments.data[key],
                isOwned: !prev.valvedInstruments.data[key].isOwned,
                price: -1,
              },
            },
          },
        }));
      } else if (title === 'Valve Felts') {
        setItems((prev) => ({
          ...prev,
          valveFelts: {
            ...prev.valveFelts,
            data: {
              ...prev.valveFelts.data,
              [key]: {
                ...prev.valveFelts.data[key],
                isOwned: !prev.valveFelts.data[key].isOwned,
                price: -1,
              },
            },
          },
        }));
      } else if (title === 'Inlays') {
        setItems((prev) => ({
          ...prev,
          inlays: {
            ...prev.inlays,
            data: {
              ...prev.inlays.data,
              [key]: {
                ...prev.inlays.data[key],
                isOwned: !prev.inlays.data[key].isOwned,
                price: -1,
              },
            },
          },
        }));
      } else if (title === 'Rotary Valves') {
        setItems((prev) => ({
          ...prev,
          rotaryValves: {
            ...prev.rotaryValves,
            data: {
              ...prev.rotaryValves.data,
              [key]: {
                ...prev.rotaryValves.data[key],
                isOwned: !prev.rotaryValves.data[key].isOwned,
                price: -1,
              },
            },
          },
        }));
      } else if (title === 'Slides') {
        setItems((prev) => ({
          ...prev,
          slides: {
            ...prev.slides,
            data: {
              ...prev.slides.data,
              [key]: {
                ...prev.slides.data[key],
                isOwned: !prev.slides.data[key].isOwned,
                price: -1,
              },
            },
          },
        }));
      }
      spendGems(obj.price);
    }

    if (obj.isOwned === true) {
      if (title === 'Valved Instruments') {
        setSelectedItems((prev) => ({
          ...prev,
          valvedInstruments: index,
        }));
      } else if (title === 'Valve Felts') {
        setSelectedItems((prev) => ({
          ...prev,
          valveFelts: index,
        }));
      } else if (title === 'Inlays') {
        setSelectedItems((prev) => ({
          ...prev,
          inlays: index,
        }));
      } else if (title === 'Rotary Valves') {
        setSelectedItems((prev) => ({
          ...prev,
          rotaryValves: index,
        }));
      } else if (title === 'Slides') {
        setSelectedItems((prev) => ({
          ...prev,
          slides: index,
        }));
      }
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div>
      <div className={`${Styles.shell}`}>
        <div className={Styles.pageWrapper}>
          <div className={Styles.headerWrapper}>
            <div className={Styles.coins}>
              <h3>{gems}</h3>
              <GemIcon size={32} />
            </div>
          </div>
          <div
            className={Styles.titleWrapper}
            style={{ backgroundImage: `url(${CosmeticsSign})` }}
          >
            <h1 className={Styles.title}>Cosmetics</h1>
          </div>
          <CosmeticsUnit
            className={Styles.cosmeticsUnit}
            title={items.valvedInstruments.title}
            data={priced(items.valvedInstruments.data)}
            selectedItem={selectedItems.valvedInstruments}
            handleClick={handleClick}
            membershipStatus={membershipStatus}
          />
          <CosmeticsUnit
            className={Styles.cosmeticsUnit}
            title={items.valveFelts.title}
            data={priced(items.valveFelts.data)}
            selectedItem={selectedItems.valveFelts}
            handleClick={handleClick}
            membershipStatus={membershipStatus}
          />
          <CosmeticsUnit
            className={Styles.cosmeticsUnit}
            title={items.inlays.title}
            data={priced(items.inlays.data)}
            selectedItem={selectedItems.inlays}
            handleClick={handleClick}
            membershipStatus={membershipStatus}
          />
          <CosmeticsUnit
            className={Styles.cosmeticsUnit}
            title={items.rotaryValves.title}
            data={priced(items.rotaryValves.data)}
            selectedItem={selectedItems.rotaryValves}
            handleClick={handleClick}
            membershipStatus={membershipStatus}
          />
          <CosmeticsUnit
            className={Styles.cosmeticsUnit}
            title={items.slides.title}
            data={priced(items.slides.data)}
            selectedItem={selectedItems.slides}
            handleClick={handleClick}
            membershipStatus={membershipStatus}
          />
          {/* Add this spacer at the very bottom */}
          <div style={{ height: '80px', width: '100%' }}></div>
        </div>
      </div>
    </div>
  );
};

export default Cosmetics;